import { Check, ChevronDown, Globe } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import type { StorefrontLanguage } from '@/features/storefront/api/storefront';
import { useShopContent } from '@/features/storefront/content/useContent';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import {
  languageLabel,
  useLanguageSwitcherVisible,
  useThemeLanguageSwitcher,
} from '@/features/storefront/i18n/switcher';
import type { LanguageDisplay } from '@/features/storefront/sections/schema';
import type { LanguageSwitcherPlacement } from '@/features/storefront/themeSchema';
import { cn } from '@/lib/cn';

/**
 * The shopper's language switcher (docs/i18n.md §7).
 *
 * Languages are named in words - their own name, their English name or their
 * code - and never with a flag: a flag is a country, and India alone would
 * need one flag for a dozen languages while Hindi would need several.
 *
 * Choosing a language calls `setLanguage` from the storefront language
 * context; the shell owns what that means (save the choice, move to the same
 * page under the language's prefix).
 */

/**
 * Whether `label` is written in the language itself, so it can carry that
 * language's `lang` attribute - which is what makes a screen reader pronounce
 * "हिन्दी" in Hindi rather than spell it out in English.
 */
function labelIsNative(display: LanguageDisplay): boolean {
  return display === 'native' || display === 'native_code';
}

export interface LanguageSwitcherProps {
  display?: LanguageDisplay;
  /** `compact` - a button and a list box, for a masthead. `list` - every language in a row. */
  variant?: 'compact' | 'list';
  className?: string;
}

export function LanguageSwitcher({
  display = 'native',
  variant = 'compact',
  className,
}: LanguageSwitcherProps) {
  const { languages, code, setLanguage } = useStorefrontLanguage();
  if (languages.length < 2) return null;
  const Variant = variant === 'list' ? LanguageList : LanguageDropdown;
  return (
    <Variant
      languages={languages}
      code={code}
      display={display}
      onChoose={setLanguage}
      className={className}
    />
  );
}

interface VariantProps {
  languages: readonly StorefrontLanguage[];
  code: string;
  display: LanguageDisplay;
  onChoose: (code: string) => void;
  className?: string;
}

function LanguageDropdown({ languages, code, display, onChoose, className }: VariantProps) {
  const text = useShopContent();
  const id = useId();
  const listId = `${id}-languages`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const currentIndex = Math.max(0, languages.findIndex((language) => language.code === code));
  const current = languages[currentIndex];

  const openList = useCallback((index: number) => {
    setActiveIndex(index);
    setOpen(true);
  }, []);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Focus follows the list open, and closing on a click anywhere else.
  useEffect(() => {
    if (!open) return;
    listRef.current?.focus();
    const onPointerDown = (event: PointerEvent | MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const choose = (index: number) => {
    const language = languages[index];
    close(true);
    if (language && language.code !== code) onChoose(language.code);
  };

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openList(currentIndex);
    }
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const last = languages.length - 1;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((index) => (index >= last ? 0 : index + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((index) => (index <= 0 ? last : index - 1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        close(true);
        break;
      case 'Tab':
        close(false);
        break;
    }
  };

  const currentLabel = languageLabel(current, display);

  return (
    <div ref={rootRef} className={cn('relative shrink-0', className)}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={text('language.current', { language: current.native_name })}
        onClick={() => (open ? close(false) : openList(currentIndex))}
        onKeyDown={onButtonKeyDown}
        className="flex items-center gap-1 text-sm font-medium"
        style={{ color: 'var(--storefront-primary)' }}
      >
        <Globe className="size-4" aria-hidden />
        <span lang={labelIsNative(display) ? current.code : undefined}>{currentLabel}</span>
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label={text('language.choose')}
          aria-activedescendant={`${id}-option-${activeIndex}`}
          onKeyDown={onListKeyDown}
          className="absolute end-0 top-full z-50 mt-2 min-w-44 overflow-hidden rounded-lg border py-1 text-sm shadow-lg outline-none"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
            color: 'var(--storefront-text)',
          }}
        >
          {languages.map((language, index) => {
            const selected = language.code === code;
            return (
              <li
                key={language.code}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={selected}
                onClick={() => choose(index)}
                onMouseEnter={() => setActiveIndex(index)}
                className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2"
                style={
                  index === activeIndex ? { backgroundColor: 'var(--storefront-surface)' } : undefined
                }
              >
                <span lang={labelIsNative(display) ? language.code : undefined}>
                  {languageLabel(language, display)}
                </span>
                {selected ? <Check className="size-4" aria-hidden /> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function LanguageList({ languages, code, display, onChoose, className }: VariantProps) {
  const text = useShopContent();
  return (
    <ul
      aria-label={text('language.choose')}
      className={cn('flex flex-wrap gap-x-4 gap-y-2 text-sm', className)}
    >
      {languages.map((language) => {
        const selected = language.code === code;
        return (
          <li key={language.code}>
            <button
              type="button"
              aria-current={selected ? 'true' : undefined}
              onClick={() => {
                if (!selected) onChoose(language.code);
              }}
              className={cn('hover:opacity-70', selected && 'font-semibold underline underline-offset-4')}
            >
              <span lang={labelIsNative(display) ? language.code : undefined}>
                {languageLabel(language, display)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The theme's switcher at one of its placements - the one line a theme's
 * header, footer or mobile sheet renders. Nothing when that placement is off.
 */
export function LanguageSwitcherSlot({
  placement,
  preview,
  className,
}: {
  placement: LanguageSwitcherPlacement;
  /** The customiser's inert chrome: no switcher. */
  preview?: boolean;
  className?: string;
}) {
  const settings = useThemeLanguageSwitcher();
  const visible = useLanguageSwitcherVisible(placement);
  if (preview || !visible) return null;
  return (
    <LanguageSwitcher
      display={settings.display}
      variant={placement === 'header' ? 'compact' : 'list'}
      className={className}
    />
  );
}
